import { Injectable, OnDestroy, inject } from '@angular/core';
import { where } from 'firebase/firestore';
import { skip, Subscription } from 'rxjs';

import { FirebaseService } from '../firebase/firebase.service';
import { OrganizationService } from '../organization/organization.service';
import { UserService } from '../user/user.service';
import { cleanBridgeError, LocalFilesService } from './local-files.service';
import { LocalFilesGrantService, describeUngranted } from './local-files-grant.service';

/**
 * The desktop half of the cloud-agent broker (native-packaging/11-local-files.md).
 *
 * A cloud agent's `local_*` tool call becomes a `localFileRequests/{id}` doc written
 * by the MCP gateway. THIS service — running only in the desktop shell, signed in as
 * the agent's owner — is what answers it: it listens for its own pending requests,
 * executes them against the shell's shared-folder grants, and writes the result the
 * gateway is awaiting.
 *
 * ## Trust and blast radius
 *
 * The query is `userId == uid`: this machine only ever sees requests addressed to its
 * signed-in user, and rules enforce the same server-side. Execution goes through the
 * same `localfiles.*` channels the page's own UI uses — grants, `resolveInside`,
 * size caps, read-only — so a malicious request doc can at worst read what the user
 * already shared, which is the granted behaviour, not an escalation.
 *
 * ## At-most-once-ish
 *
 * Two desktops signed into the same account could both answer. Reads are idempotent
 * and rules only allow pending→done/error, so the second write fails quietly — noted,
 * not engineered around. `answering` guards the same request being answered twice by
 * THIS client while its first answer is in flight.
 */
@Injectable({ providedIn: 'root' })
export class LocalFileBrokerService implements OnDestroy {
  private readonly firebase = inject(FirebaseService);
  private readonly organization = inject(OrganizationService);
  private readonly userService = inject(UserService);
  private readonly localFiles = inject(LocalFilesService);
  private readonly grants = inject(LocalFilesGrantService);

  private unsubscribe: (() => void) | null = null;
  private regionSub: Subscription | null = null;
  private readonly answering = new Set<string>();

  async start(): Promise<void> {
    if (!this.localFiles.supported()) return;
    if (this.unsubscribe) return;
    // Same readiness gate as the rail: auth + org resolved before any listener.
    await this.organization.isReady();
    const uid = this.userService.user?.uid ?? this.firebase.auth?.auth?.currentUser?.uid;
    if (this.unsubscribe) return;
    if (!uid) {
      // org.isReady() can resolve before the user object lands (and a signed-out
      // boot has no user at all). The old one-shot read gave up FOREVER here,
      // silently — every cloud file request then timed out as "the desktop app
      // isn't running" (measured 2026-09-24). Re-arm on the next auth state
      // instead, and say so.
      console.warn('[local-files] broker: no signed-in user yet — will start on auth state');
      const off = this.firebase.auth.onAuthStateChanged((user) => {
        if (!user) return;
        off();
        void this.start();
      });
      return;
    }

    // Identity + liveness in one greppable line: "requests never answered" has now
    // been three different bugs (rules, dead listener, and possibly a shell signed
    // in as a different account than the asker) — this line settles which one in a
    // single console read, with the email for human comparison.
    console.info(`[local-files] broker listening for uid ${uid} (${this.userService.user?.email ?? 'no email'})`);

    // Diagnostic sweep (2026-09-24): dump this user's recent request docs so a
    // failed attempt can be autopsied from the console — the doc's very existence,
    // uid, and final status distinguish a writer-side fault from a listener-side
    // one without server log access. Owner-only read; cheap; remove once the
    // broker has been boring for a while.
    void this.firebase.firestore
      .collectionGet('localFileRequests', where('userId', '==', uid))
      .then((snapshot) => {
        const rows = snapshot.docs
          .map((d) => {
            const data = d.data() as Record<string, unknown>;
            return {
              id: d.id,
              tool: String(data.tool ?? ''),
              source: String(data.source ?? ''),
              status: String(data.status ?? ''),
              ageSec: Math.round((Date.now() - Number(data.createdAt ?? 0)) / 1000),
            };
          })
          .sort((a, b) => a.ageSec - b.ageSec)
          .slice(0, 10);
        console.info(`[local-files] recent requests for this uid: ${rows.length}`);
        if (rows.length) console.table(rows);
      })
      .catch((err) => console.warn('[local-files] request sweep failed:', err));

    // Data residency: requests are created in the active org's regional
    // project — re-listen there when the org handles move to another region.
    this.regionSub ??= this.firebase.region$.pipe(skip(1)).subscribe(() => {
      if (!this.unsubscribe) return;
      this.unsubscribe();
      this.unsubscribe = null;
      void this.start();
    });
    this.unsubscribe = this.firebase.firestore.collectionListen(
      'localFileRequests',
      [where('userId', '==', uid), where('status', '==', 'pending')],
      (snapshot) => {
        if (!snapshot.docs.length) return;
        console.info(`[local-files] broker received ${snapshot.docs.length} pending request(s)`);
        for (const doc of snapshot.docs) void this.answer(doc.id, doc.data() as Record<string, unknown>);
      },
    );
  }

  ngOnDestroy(): void {
    this.regionSub?.unsubscribe();
    this.regionSub = null;
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  private async answer(id: string, request: Record<string, unknown>): Promise<void> {
    if (this.answering.has(id)) return;
    this.answering.add(id);
    const startedAt = Date.now();
    try {
      const origin = await this.askerLabel(request);
      const result = await this.execute(String(request.tool ?? ''), (request.input ?? {}) as Record<string, unknown>, origin);
      await this.firebase.firestore.docUpdate(`localFileRequests/${id}`, {
        status: 'done',
        result,
        answeredAt: Date.now(),
      });
      console.info(`[local-files] answered ${id} in ${Date.now() - startedAt}ms`);
    } catch (err) {
      // permission-denied here is the LOST-RACE shape, not a fault: another desktop
      // (or the function's own timeout) already moved the doc off 'pending', and the
      // update rule refuses non-pending docs by design. One line, not error spam.
      if ((err as { code?: string })?.code === 'permission-denied') {
        console.info(`[local-files] ${id} was already answered elsewhere (or timed out server-side) — skipped after ${Date.now() - startedAt}ms`);
        return;
      }
      // Loud on purpose: a silent error-write made an image-read failure look like a
      // hung request (measured 2026-09-25 — 'received' with no 'answered' line).
      console.warn(`[local-files] ${String(request.tool ?? '?')} ${id} failed after ${Date.now() - startedAt}ms:`, err);
      await this.firebase.firestore
        .docUpdate(`localFileRequests/${id}`, {
          status: 'error',
          error: cleanBridgeError(err),
          answeredAt: Date.now(),
        })
        .catch(() => undefined); // raced another desktop, or the request timed out server-side
    } finally {
      this.answering.delete(id);
    }
  }

  /**
   * Who is asking, for the grant card (#6): a cloud agent by NAME (its request carries
   * agentId; the owner can read the agent doc), a cloud chat as such. Every cloud ask
   * used to read "an agent", so a chat looked like an agent and, in a team, you could
   * not tell which agent wanted your files. '@'-prefixed values are i18n keys.
   */
  private async askerLabel(request: Record<string, unknown>): Promise<string> {
    const agentId = typeof request.agentId === 'string' ? request.agentId : '';
    if (agentId) {
      try {
        const snap = await this.firebase.firestore.docGet(`agents/${agentId}`);
        const name = snap.data()?.agentName;
        if (typeof name === 'string' && name.trim()) return name.trim();
      } catch {
        // fall through to the generic label
      }
      return '@agent';
    }
    return '@cloud-chat';
  }

  private async execute(tool: string, input: Record<string, unknown>, origin = '@agent'): Promise<unknown> {
    const root = String(input.root ?? '');

    // Just-in-time grants for the CLOUD paths (agents + cloud chats): a brokered ask
    // (local_request_access), or any call arriving with nothing shared, raises the
    // same app-level card local chats use. The broker's server-side timeout bounds
    // the wait; a slow human's grant answers the model's retry.
    if (tool === 'local_request_access') {
      const asked = String(input.path ?? '').trim();
      const looksLikePath = asked.startsWith('/') || /^[A-Za-z]:[\\/]/.test(asked);
      const beforePaths = new Set(this.localFiles.roots().map((r) => r.path));
      const outcome = await this.grants.requestAccessOutcome(origin, looksLikePath ? asked : undefined);
      if (outcome !== 'granted') return describeUngranted(outcome);
      await this.localFiles.refresh();
      // Report the DELTA of this ask; the model already knows (or can list) the
      // rest, and "user shared: <eight folders>" for a one-folder grant read as
      // an access leak (2026-09-27).
      const added = this.localFiles.roots().filter((r) => !beforePaths.has(r.path));
      if (added.length > 0) {
        return `The user shared: ${added.map((r) => `${r.path} (shared as "${r.name}")`).join('; ')}`;
      }
      return 'Access granted. Use local_list_shared_folders to see the shared folders.';
    }
    if (tool !== 'local_list_shared_folders' || this.localFiles.roots().length === 0) {
      await this.localFiles.refresh();
    }
    if (this.localFiles.roots().length === 0) {
      const outcome = await this.grants.requestAccessOutcome(origin);
      if (outcome !== 'granted') return `No folders are shared on this computer. ${describeUngranted(outcome)}`;
      await this.localFiles.refresh();
    }

    switch (tool) {
      case 'local_list_shared_folders': {
        await this.localFiles.refresh();
        const roots = this.localFiles.roots();
        if (roots.length === 0) {
          return 'No folders are shared on this computer. The user can share one in Crewdle Settings → Shared folders.';
        }
        // Path FIRST: `root` in every other tool is the path, and models copy
        // whatever leads the line — "Name: path" produced root="Lapointe_Thomas"
        // (measured 2026-09-25).
        return this.localFiles.describeRoots();
      }
      case 'local_list_files':
        return this.localFiles.list(this.localFiles.resolveRoot(root), input.path ? String(input.path) : undefined);
      case 'local_read_file': {
        const filePath = String(input.path ?? '');
        // Images: the model needs PIXELS, not a binary-file refusal. The shell
        // returns a downscaled JPEG; the __localImage envelope tells the chat
        // function to attach it as an image part for the model's next turn.
        const isImage = /\.(png|jpe?g|webp|gif|bmp|ico)$/i.test(filePath);
        if (isImage && !this.localFiles.supportsImages()) {
          // Say the true reason: the text reader's "binary file" refusal reads as a
          // permanent limitation, when the actual fix is an app update.
          return 'This file is an image, but the installed Crewdle desktop app is too old to read images. Ask the user to update the app, then try again.';
        }
        if (isImage) {
          const image = await this.localFiles.readImage(this.localFiles.resolveRoot(root), filePath);
          return {
            // Envelope key must survive lint's naming rules — it is a wire tag, not a property.
            // eslint-disable-next-line @typescript-eslint/naming-convention
            __localImage: {
              mediaType: image.mediaType,
              data: image.data,
              width: image.width,
              height: image.height,
              sizeBytes: image.sizeBytes,
              name: filePath,
            },
          };
        }
        return this.localFiles.read(this.localFiles.resolveRoot(root), filePath, {
          offset: input.offset as number | undefined,
          length: input.length as number | undefined,
        });
      }
      case 'local_search_files':
        return this.localFiles.search(this.localFiles.resolveRoot(root), String(input.query ?? ''));
      default:
        throw new Error(`Unknown local files tool "${tool}"`);
    }
  }
}
